/*
 * simple router derived from https://github.com/FormidableLabs/redux-little-router
 * Contains only the subset of Redux Little Router used in the UIX app
 *
 * This lean version focuses on browser/express installers, reducer, middleware,
 * and the push/replace/initializeCurrentLocation action creators that UIX actually invokes.
 * The vendored original supported additional behaviour that we currently omit:
 *  - Navigation queues that buffered multiple in-flight actions before LOCATION_CHANGED.
 *  - Automatic query persistence via action.payload.options.persistQuery merges.
 *  - History block/unblock helpers (BLOCK/UNBLOCK) to guard transitions.
 *  - Generic GO/GO_BACK/GO_FORWARD action handling for imperative history jumps.
 *  - Route replacement APIs (REPLACE_ROUTES/DID_REPLACE_ROUTES) and matcher cache swaps.
 *  - UrlPattern-driven wildcard matching for arbitrary route hierarchies.
 *  - Match caches that memoised parent-child relationships for nested routes.
 *
 * Note: The matcher here understands only the default PATTERN from src/router/routing.js (p0-p6).
 * Adding new route keys or wildcard patterns requires reinstating UrlPattern-based matching.
 * Likewise, re-enabling query persistence or history blocking will need the helper utilities
 * (merge-queries.js, match-cache.js, etc.) from the original vendor bundle.
 */ import { createBrowserHistory, createMemoryHistory, parsePath } from 'history';
import { parseQuery, stringifyQuery } from './query';
const LOCATION_CHANGED = 'ROUTER_LOCATION_CHANGED';
const PUSH = 'ROUTER_PUSH';
const REPLACE = 'ROUTER_REPLACE';
const POP = 'ROUTER_POP';
export const DEFAULT_ROUTE_PATTERN = '/(:p0)(/)(:p1)(/)(/:p2)(/)(/:p3)(/)(/:p4)(/)(/:p5)(/)(/:p6)(/)';
const MAX_ROUTE_SEGMENTS = 7;
const normalizeHref = (href)=>{
    if (typeof href === 'string') {
        const { search = '', ...other } = parsePath(href);
        const query = search && parseQuery(search);
        return query ? {
            ...other,
            query,
            search
        } : {
            ...other
        };
    }
    const { search, query } = href;
    const resolvedSearch = search || query && Object.keys(query).length && `?${stringifyQuery(query)}` || '';
    const resolvedQuery = query || parseQuery(search || '');
    return {
        ...href,
        search: resolvedSearch,
        query: resolvedQuery
    };
};
export const parseDefaultRoute = (pathname)=>{
    const params = {};
    const length = pathname.length;
    let index = 0;
    let start = 0;
    while(start < length && pathname.charCodeAt(start) === 47){
        start += 1;
    }
    let i = start;
    while(index < MAX_ROUTE_SEGMENTS && i <= length){
        if (i === length || pathname.charCodeAt(i) === 47) {
            const segment = pathname.slice(start, i);
            params[`p${index}`] = segment ? segment : undefined;
            index += 1;
            while(i < length && pathname.charCodeAt(i) === 47){
                i += 1;
            }
            start = i;
        } else {
            i += 1;
        }
    }
    while(index < MAX_ROUTE_SEGMENTS){
        params[`p${index}`] = undefined;
        index += 1;
    }
    return params;
};
const matchDefaultRoute = (pathname, routeConfig)=>({
        route: DEFAULT_ROUTE_PATTERN,
        params: parseDefaultRoute(pathname),
        result: routeConfig
    });
const createRouterReducer = ({ initialLocation } = {})=>(state = {
        ...initialLocation
    }, action)=>{
        if (action.type !== LOCATION_CHANGED) {
            return state;
        }
        if (state.pathname === action.payload.pathname && state.search === action.payload.search && state.hash === action.payload.hash) {
            return state;
        }
        const { previous, ...rest } = state;
        return {
            ...action.payload,
            previous: rest
        };
    };
const createRouterMiddleware = ({ history })=>()=>(next)=>(action)=>{
                const result = next(action);
                if (action.type === PUSH) {
                    history.push(action.payload);
                } else if (action.type === REPLACE) {
                    history.replace(action.payload);
                }
                return result;
            };
const locationDidChange = (location)=>({
        type: LOCATION_CHANGED,
        payload: location
    });
const createRouterEnhancer = ({ history, matchRoute })=>(createStore)=>(userReducer, initialState, enhancer)=>{
            const store = createStore(userReducer, initialState, enhancer);
            history.listen((location, action)=>{
                const match = matchRoute(location.pathname);
                const payload = {
                    ...location,
                    ...match || {},
                    query: parseQuery(location.search || '')
                };
                if (action === 'POP') {
                    store.dispatch({
                        type: POP,
                        payload
                    });
                }
                store.dispatch(locationDidChange(payload));
            });
            return store;
        };
const ensureDefaultRoute = (routes)=>{
    if (!routes || !routes[DEFAULT_ROUTE_PATTERN]) {
        throw new Error('Expected default catch-all route pattern. See src/router/routing.js for details.');
    }
    return routes[DEFAULT_ROUTE_PATTERN];
};
const install = ({ reducer, middleware, enhancer })=>({ routes: nestedRoutes, history, location })=>{
        const routeConfig = ensureDefaultRoute(nestedRoutes);
        const matchRoute = (pathname)=>matchDefaultRoute(pathname, routeConfig);
        const initialLocation = {
            ...location,
            ...matchRoute(location.pathname)
        };
        return {
            reducer: reducer({
                initialLocation
            }),
            middleware: middleware({
                history
            }),
            enhancer: enhancer({
                history,
                matchRoute
            })
        };
    };
const createRouterForBrowser = (installer)=>({ routes, basename, historyOptions = {}, history = createBrowserHistory({
        basename,
        ...historyOptions
    }) })=>{
        const { pathname: fullPathname, search, hash, state: { key, state } = {} } = history.location;
        const pathname = basename && fullPathname.indexOf(basename) === 0 ? fullPathname.slice(basename.length) : fullPathname;
        const descriptor = basename ? {
            pathname,
            basename,
            search,
            hash,
            key,
            state
        } : {
            pathname,
            search,
            hash,
            key,
            state
        };
        const location = normalizeHref(descriptor);
        return installer({
            routes,
            history,
            location
        });
    };
const createRouterForExpress = (installer)=>({ routes, request, historyOptions = {} })=>{
        const history = createMemoryHistory(historyOptions);
        const descriptor = request.baseUrl ? {
            pathname: request.path,
            basename: request.baseUrl,
            query: request.query
        } : {
            pathname: request.path,
            query: request.query
        };
        const location = normalizeHref(descriptor);
        return installer({
            routes,
            history,
            location
        });
    };
const routerInstaller = install({
    reducer: createRouterReducer,
    middleware: createRouterMiddleware,
    enhancer: createRouterEnhancer
});
const routerForBrowser = createRouterForBrowser(routerInstaller);
const routerForExpress = createRouterForExpress(routerInstaller);
export const push = (href, options = {})=>({
        type: PUSH,
        payload: {
            ...normalizeHref(href),
            options
        }
    });
export const replace = (href, options = {})=>({
        type: REPLACE,
        payload: {
            ...normalizeHref(href),
            options
        }
    });
export const initializeCurrentLocation = (location)=>({
        type: LOCATION_CHANGED,
        payload: location
    });
export { routerForBrowser, routerForExpress };
